import { GQLContentPageFragment } from '@theprintbar/graphql';
import { BrandColours, cn } from '@theprintbar/shared';
import { ButtonSize, OutlineLinkButton, PromoImageContainer, SmallArrowIcon } from '@theprintbar/ui';
import { CTAContainer } from './CTAContainer';
import { CTAText } from './CTAText';

interface CTAProps {
	cta?: GQLContentPageFragment['roundedCTA'];
	ctaClassName?: string;
	gravityTitle?: boolean;
	containerClassName?: string;
	imageContainerClassName?: string;
	split?: boolean;
	reversed?: boolean;
	even?: boolean;
	pill?: {
		text: string;
		colour: string;
	};
}

export const RoundedCTASkeleton = ({ split, ctaClassName, containerClassName }: CTAProps) => {
	return (
		<CTAContainer className={cn('py-0 lg:my-0 my-0', containerClassName)}>
			<div
				className={cn(
					'w-full flex flex-col lg:flex-row rounded-3xl overflow-hidden bg-grey/10 animate-pulse mt-4',
					split && 'grid grid-cols-2 gap-4 flex-none bg-transparent',
				)}
			>
				{/* CTA Container */}
				<div className="relative w-full aspect-[5/3] max-h-[32rem] p-7 lg:p-12 bg-grey/20 rounded-3xl">
					{/* Content Container */}
					<div className="relative lg:max-w-lg space-y-6">
						<div className="h-2.5 bg-grey/10 rounded-full w-48 mb-4"></div>
						<div className="h-2 bg-grey/10 rounded-full max-w-[480px] mb-2.5"></div>
						<div className="h-2 bg-grey/10 rounded-full mb-2.5"></div>
						<div className="h-2 bg-grey/10 rounded-full max-w-[440px] mb-2.5"></div>
					</div>
				</div>
				{/* Image Container */}
				<div className="relative w-full flex justify-center items-center aspect-[5/3] p-7 lg:p-12 bg-grey/10 rounded-3xl">
					<svg
						className="w-10 h-10 text-grey/30 "
						aria-hidden="true"
						xmlns="http://www.w3.org/2000/svg"
						fill="currentColor"
						viewBox="0 0 20 18"
					>
						<path d="M18 0H2a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V2a2 2 0 0 0-2-2Zm-5.5 4a1.5 1.5 0 1 1 0 3 1.5 1.5 0 0 1 0-3Zm4.376 10.481A1 1 0 0 1 16 15H4a1 1 0 0 1-.895-1.447l3.5-7A1 1 0 0 1 7.468 6a.965.965 0 0 1 .9.5l2.775 4.757 1.546-1.887a1 1 0 0 1 1.618.1l2.541 4a1 1 0 0 1 .028 1.011Z" />
					</svg>
				</div>
			</div>
		</CTAContainer>
	);
};

export function RoundedCTA({
	cta,
	ctaClassName,
	containerClassName,
	imageContainerClassName,
	gravityTitle,
	split,
	reversed,
	even,
	pill,
}: CTAProps) {
	if (!cta?.structure) return <RoundedCTASkeleton split />;

	const { title, text, colour, icon, image, links, sticker } = cta.structure;

	if (!title && !text && !links) return null;

	return (
		<CTAContainer
			sticker={sticker}
			pill={pill}
			pillClassName="font-sans text-xs"
			stickerPosition={split ? 'bottom-right' : 'bottom-middle'}
			className={cn('py-0 lg:my-0 my-0', containerClassName)}
		>
			<div
				className={cn(
					'w-full flex flex-col lg:flex-row border border-blue rounded-3xl overflow-hidden',
					!!pill && 'mt-4',
					!!reversed && 'flex-col-reverse lf:flex-row-reverse',
					!!split && 'grid flex-none gap-7 border-none',
					!!split && even && 'lg:grid-cols-8',
					!!split && !even && 'lg:grid-cols-7',
				)}
				style={{
					backgroundColor: split ? 'transparent' : colour || BrandColours.Yellow,
				}}
			>
				{/* CTA Container */}
				<div
					className={cn(
						'relative w-full lg:w-[50%] h-fit lg:min-h-96 p-7 lg:p-12 2xl:pr-36 lg:col-span-4',
						split && 'rounded-3xl border border-blue lg:w-full',
						split && reversed && 'order-2 lg:order-2 lg:w-full',
						ctaClassName,
					)}
				>
					{/* Content Container */}
					<div className="relative space-y-6">
						<h2
							className={cn(
								gravityTitle
									? 'text-4xl lg:text-5xl font-gravity'
									: 'lg:max-w-md text-2xl lg:text-3xl font-display font-light ',
							)}
						>
							{title}
						</h2>
						<CTAText className="text-sm w-full lg:text-base" text={text} />
						{links?.map(link =>
							link ? (
								<OutlineLinkButton
									className="w-fit uppercase font-belfast !font-bold text-xs"
									to={link.to || '#'}
									icon={SmallArrowIcon}
									iconSize={ButtonSize.S}
									key={link.text}
								>
									{link.text || 'Read More'}
								</OutlineLinkButton>
							) : null,
						)}
					</div>
				</div>
				{/* Image Container */}
				<div
					className={cn(
						'lg:w-[50%] w-full h-64 lg:h-auto border-t lg:border-l lg:border-t-0 border-blue',
						even ? 'lg:col-span-4' : 'lg:col-span-3',
						reversed && 'lg:border-r',
						split && 'rounded-3xl border border-blue overflow-hidden lg:w-full h-full',
						split && reversed && 'order-1 lg:order-1 lg:w-full h-full',
						imageContainerClassName,
					)}
				>
					<PromoImageContainer promoImage={image} className="w-full h-full overflow-hidden" />
				</div>
			</div>
		</CTAContainer>
	);
}
